import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  KeyIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ServerStackIcon,
  EyeIcon,
  EyeSlashIcon,
  WifiIcon,
  ClockIcon,
  CogIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { platformService, Platform } from '../../services/platformService';
import { cachePreferencesService } from '../../services/cachePreferencesService';
import { usePreloadStore } from '../../store/preloadStore';
import PlatformLogo from './PlatformLogo';
import { getPlatformDisplayName } from '../../utils/platformUtils';
import { UserBrowserCredentialService } from '../../services/userBrowserCredentialService';
import PlatformCredentialsTab from './PlatformCredentialsTab';

interface PlatformCredentialsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CredentialForm {
  auth_client_id: string;
  auth_client_secret: string;
  auth_account_id: string;
}

type TabType = 'organization' | 'user';

const PlatformCredentialsModal: React.FC<PlatformCredentialsModalProps> = ({ isOpen, onClose }) => {
  const [selectedPlatformId, setSelectedPlatformId] = useState<number | null>(null);
  const [credentialForms, setCredentialForms] = useState<Record<number, CredentialForm>>({});
  const [showSecrets, setShowSecrets] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Auto-dismiss success/error messages after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000); // Errors stay a bit longer
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Cache preferences state
  const [cacheEnabled, setCacheEnabled] = useState<Record<number, boolean>>({});
  const [cacheDuration, setCacheDuration] = useState<Record<number, number>>({});
  const [hasCustomCacheSettings, setHasCustomCacheSettings] = useState<Record<number, boolean>>({});
  const [savingCache, setSavingCache] = useState<Record<number, boolean>>({});



  // Helper function to check if credentials are configured based on auth type
  const hasCredentialsConfigured = (platform: Platform): boolean => {
    switch (platform.auth_type) {
      case 'api_key':
        return !!platform.auth_client_secret;
      case 'user_oauth':
        return !!(platform.auth_client_id && platform.auth_client_secret);
      case 's2s_oauth':
      default:
        return !!(platform.auth_client_id && platform.auth_client_secret && platform.auth_account_id);
    }
  };

  // Get platforms from preload store and filter out zoom_users
  const { getPlatforms, getUserInfo, isLoading, error: preloadError, refreshPreload } = usePreloadStore();
  const allPlatforms = getPlatforms();
  const platforms = useMemo(() => 
    allPlatforms.filter(platform => platform.name.toLowerCase() !== 'zoom_users'),
    [allPlatforms]
  );
  const isError = !!preloadError;
  
  // Get user info to check staff status
  const userInfo = getUserInfo();
  const isStaff = userInfo?.user?.is_staff || false;

  // Initialize forms when platforms are loaded
  useEffect(() => {
    if (platforms.length > 0) {
      const initialForms: Record<number, CredentialForm> = {};
      platforms.forEach(platform => {
        initialForms[platform.id] = {
          auth_client_id: platform.auth_client_id || '',
          auth_client_secret: '', // Never prefill secrets
          auth_account_id: platform.auth_account_id || '',
        };
      });
      setCredentialForms(initialForms);
      
      // Load cache preferences for all platforms
      loadAllCachePreferences();
      
      // Set first platform as selected if none selected (only on initial load)
      if (!selectedPlatformId && platforms.length > 0) {
        setSelectedPlatformId(platforms[0].id);
      }
    }
  }, [platforms]); // Removed selectedPlatformId from dependencies to prevent infinite loop

  // Handle body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const loadAllCachePreferences = async () => {
    try {
      const response = await cachePreferencesService.getPlatformSettings();
      const platformPreferences = response.platform_preferences;
      
      const cacheEnabledMap: Record<number, boolean> = {};
      const cacheDurationMap: Record<number, number> = {};
      const hasCustomMap: Record<number, boolean> = {};
      
      platforms.forEach(platform => {
        const preference = platformPreferences.find(p => p.platform_id === platform.id);
        if (preference) {
          cacheEnabledMap[platform.id] = preference.cache_enabled;
          cacheDurationMap[platform.id] = preference.cache_duration_days;
          hasCustomMap[platform.id] = preference.has_custom_settings;
        } else {
          // Use global defaults - these will be loaded from global settings
          cacheEnabledMap[platform.id] = true; // Default enabled
          cacheDurationMap[platform.id] = 7; // Default 7 days
          hasCustomMap[platform.id] = false;
        }
      });
      
      setCacheEnabled(cacheEnabledMap);
      setCacheDuration(cacheDurationMap);
      setHasCustomCacheSettings(hasCustomMap);
    } catch (err: any) {
      console.error('Failed to load cache preferences:', err);
    }
  };

  const handleInputChange = (platformId: number, field: keyof CredentialForm, value: string) => {
    setCredentialForms(prev => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        [field]: value
      }
    }));
  };

  const toggleSecretVisibility = (platformId: number) => {
    setShowSecrets(prev => ({
      ...prev,
      [platformId]: !prev[platformId]
    }));
  };

  const handleSave = async (platformId: number) => {
    const form = credentialForms[platformId];
    const platform = platforms.find(p => p.id === platformId);
    if (!form || !platform) return;

    try {
      setSaving(prev => ({ ...prev, [platformId]: true }));
      setError(null);
      setSuccess(null);

      // Build update data with required platform fields and credentials
      const updateData: any = {
        // Include required platform fields to satisfy backend validation
        name: platform.name,
        api_url: platform.api_url,
        is_production_enabled: platform.is_production_enabled,
      };

      // Only include credential fields that have values
      if (form.auth_client_id.trim()) updateData.auth_client_id = form.auth_client_id.trim();
      if (form.auth_client_secret.trim()) updateData.auth_client_secret = form.auth_client_secret.trim();
      if (form.auth_account_id.trim()) updateData.auth_account_id = form.auth_account_id.trim();

      // Check if at least one credential field has a value
      const hasCredentials = form.auth_client_id.trim() || form.auth_client_secret.trim() || form.auth_account_id.trim();
      if (!hasCredentials) {
        setError('Please fill in at least one credential field');
        return;
      }

      await platformService.updatePlatform(platformId, updateData);
      
      // Refresh platforms data
      await refreshPreload();
      
      setSuccess('Platform credentials updated successfully!');
      
      // Clear the client secret field after successful save
      setCredentialForms(prev => ({
        ...prev,
        [platformId]: {
          ...prev[platformId],
          auth_client_secret: ''
        }
      }));
      
    } catch (err: any) {
      setError(err.message || 'Failed to update platform credentials');
    } finally {
      setSaving(prev => ({ ...prev, [platformId]: false }));
    }
  };

  const handleSaveCachePreferences = async (platformId: number) => {
    try {
      setSavingCache(prev => ({ ...prev, [platformId]: true }));
      setError(null);
      setSuccess(null);

      const response = await cachePreferencesService.updatePlatformSettings({
        platform_id: platformId,
        cache_enabled: cacheEnabled[platformId],
        cache_duration_days: cacheDuration[platformId]
      });

      setSuccess('Cache preferences updated successfully!');
      setHasCustomCacheSettings(prev => ({ ...prev, [platformId]: true }));
      
    } catch (err: any) {
      setError(err.message || 'Failed to update cache preferences');
    } finally {
      setSavingCache(prev => ({ ...prev, [platformId]: false }));
    }
  };

  const handleRemoveCachePreferences = async (platformId: number) => {
    try {
      setSavingCache(prev => ({ ...prev, [platformId]: true }));
      setError(null);
      setSuccess(null);

      const response = await cachePreferencesService.removePlatformSettings({ platform_id: platformId });
      
      setSuccess('Cache preferences reset to global defaults!');
      setHasCustomCacheSettings(prev => ({ ...prev, [platformId]: false }));
      
      // Reload to get updated settings
      await loadAllCachePreferences();
      
    } catch (err: any) {
      setError(err.message || 'Failed to reset cache preferences');
    } finally {
      setSavingCache(prev => ({ ...prev, [platformId]: false }));
    }
  };

  const selectedPlatform = platforms.find(p => p.id === selectedPlatformId);
  const selectedForm = selectedPlatformId ? credentialForms[selectedPlatformId] : null;

  // Helper function to get field configuration based on auth type
  const getFieldConfig = (authType: string) => {
    switch (authType) {
      case 'api_key':
        return {
          fields: ['auth_client_secret'],
          labels: {
            auth_client_secret: 'API Key'
          },
          placeholders: {
            auth_client_secret: 'Enter your API key'
          },
          descriptions: {
            auth_client_secret: 'The API key provided by your platform'
          }
        };
      case 'user_oauth':
        return {
          fields: ['auth_client_id', 'auth_client_secret'],
          labels: {
            auth_client_id: 'Client ID',
            auth_client_secret: 'Client Secret'
          },
          placeholders: {
            auth_client_id: 'Enter OAuth client ID',
            auth_client_secret: 'Enter OAuth client secret'
          },
          descriptions: {
            auth_client_id: 'OAuth application client ID',
            auth_client_secret: 'OAuth application client secret'
          }
        };
      case 's2s_oauth':
      default:
        return {
          fields: ['auth_client_id', 'auth_client_secret', 'auth_account_id'],
          labels: {
            auth_client_id: 'Client ID',
            auth_client_secret: 'Client Secret',
            auth_account_id: 'Account ID'
          },
          placeholders: {
            auth_client_id: 'Enter OAuth client ID',
            auth_client_secret: 'Enter OAuth client secret',
            auth_account_id: 'Enter account ID'
          },
          descriptions: {
            auth_client_id: 'Server-to-server OAuth client ID',
            auth_client_secret: 'Server-to-server OAuth client secret',
            auth_account_id: 'Platform account identifier'
          }
        };
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="relative bg-theme-bg dark:bg-dark-theme-bg rounded-lg shadow-2xl w-[95vw] h-[85vh] max-w-6xl overflow-hidden border border-theme-border dark:border-dark-theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full md:flex-row">
          {/* Sidebar */}
          <div className="flex flex-col flex-shrink-0 p-4 w-full border-b md:w-64 bg-theme-bg dark:bg-dark-theme-bg md:rounded-l-lg md:p-6 md:border-b-0 md:border-r border-theme-border dark:border-dark-theme-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
                Platform Credentials
              </h2>
              <button
                onClick={onClose}
                className="text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text"
                aria-label="Close platform credentials"
                title="Close platform credentials modal"
              >
                <XMarkIcon className="w-6 h-6" aria-hidden="true" />
              </button>
            </div>



            {/* Platform Navigation */}
            <div className="overflow-hidden flex-1">
              <nav className="space-y-2 h-full overflow-y-auto pr-2 max-h-[calc(100vh-12rem)]">
                {isLoading ? (
                  <div className="flex justify-center items-center p-8">
                    <div className="w-8 h-8 rounded-full border-b-2 animate-spin border-primary-light"></div>
                  </div>
                ) : isError ? (
                  <div className="p-4 text-center text-red-500">
                    Failed to load platforms
                  </div>
                ) : platforms.length === 0 ? (
                  <div className="p-4 text-center text-theme-text-secondary dark:text-dark-theme-text-secondary">
                    No platforms found
                  </div>
                ) : (
                  platforms.map((platform) => (
                    <button
                      key={platform.id}
                      onClick={() => setSelectedPlatformId(platform.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left transition-colors ${
                        selectedPlatformId === platform.id
                          ? 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark'
                          : 'text-theme-text-secondary dark:text-dark-theme-text-secondary hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary'
                      }`}
                    >
                      <PlatformLogo 
                        platformName={platform.name} 
                        size="sm"
                        logoBase64={platform.logo_base64}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{getPlatformDisplayName(platform.name)}</div>
                        <div className="flex items-center space-x-2">
                          {platform.is_production_enabled && (
                            <span className="inline-flex items-center text-xs text-green-700 bg-green-100 rounded font-small dark:bg-green-900/30 dark:text-green-300">
                              Production
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        {hasCredentialsConfigured(platform) && (
                          <div 
                            className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full"
                            title="Credentials configured"
                          ></div>
                        )}
                        {platform.is_production_enabled && (
                          <div 
                            className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full"
                            title="Production enabled"
                          ></div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </nav>
            </div>
          </div>

           {/* Main Content */}
           <div className="overflow-y-auto flex-1 p-4 md:p-6">
             {/* Success/Error Messages */}
             {success && (
               <div className="p-4 mb-6 bg-green-50 rounded-md border border-green-200 dark:bg-green-900/20 dark:border-green-800">
                 <div className="flex items-center">
                   <CheckCircleIcon className="mr-2 w-5 h-5 text-green-500" />
                   <p className="text-green-700 dark:text-green-300">{success}</p>
                 </div>
               </div>
             )}

             {error && (
               <div className="p-4 mb-6 bg-red-50 rounded-md border border-red-200 dark:bg-red-900/20 dark:border-red-800">
                 <div className="flex items-center">
                   <ExclamationTriangleIcon className="mr-2 w-5 h-5 text-red-500" />
                   <p className="text-red-700 dark:text-red-300">{error}</p>
                 </div>
               </div>
             )}

             {/* Platform Details */}
             {selectedPlatform && selectedForm ? (
               <div className="space-y-6">
                 <div className="flex items-center space-x-3">
                   <PlatformLogo
                     platformName={selectedPlatform.name}
                     size="lg"
                     logoBase64={selectedPlatform.logo_base64}
                   />
                   <div>
                     <h3 className="text-2xl font-semibold text-theme-text dark:text-dark-theme-text">
                       {getPlatformDisplayName(selectedPlatform.name)}
                     </h3>
                     <div className="flex items-center space-x-2">
                       <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                         selectedPlatform.auth_type === 'api_key'
                           ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                           : selectedPlatform.auth_type === 'user_oauth'
                           ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                           : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                       }`}>
                         {selectedPlatform.auth_type === 'api_key'
                           ? 'API Key'
                           : selectedPlatform.auth_type === 'user_oauth'
                           ? 'User OAuth'
                           : 'Server-to-Server OAuth'
                         }
                       </span>
                     </div>
                   </div>
                 </div>

                 {/* Organization Credentials Section */}
                 <div className="p-6 rounded-lg border bg-theme-bg dark:bg-dark-theme-bg border-theme-border dark:border-dark-theme-border">
                   <div className="flex items-center mb-4 space-x-3">
                     <KeyIcon className="w-6 h-6 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                     <h4 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
                       Organization Authentication Credentials
                     </h4>
                   </div>

                  {(() => {
                    const config = getFieldConfig(selectedPlatform.auth_type);
                    return (
                      <div className="space-y-4">
                        {config.fields.map((fieldKey) => {
                          const isSecretField = fieldKey === 'auth_client_secret';
                          return (
                            <div key={fieldKey}>
                              <div className="flex items-center mb-2 space-x-2">
                                <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary">
                                  {config.labels[fieldKey as keyof typeof config.labels]}
                                </label>
                                {isSecretField && hasCredentialsConfigured(selectedPlatform) && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                    <CheckCircleIcon className="mr-1 w-3 h-3" />
                                    Configured
                                  </span>
                                )}
                              </div>
                              <div className="relative">
                                <input
                                  type={isSecretField && showSecrets[selectedPlatform.id] ? 'text' : isSecretField ? 'password' : 'text'}
                                  value={selectedForm[fieldKey as keyof typeof selectedForm]}
                                  onChange={(e) => handleInputChange(selectedPlatform.id, fieldKey as keyof CredentialForm, e.target.value)}
                                  className={`w-full px-4 py-2 ${isSecretField ? 'pr-10' : ''} rounded-md border border-theme-border dark:border-dark-theme-border bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent`}
                                  placeholder={
                                    isSecretField && hasCredentialsConfigured(selectedPlatform) 
                                      ? `Update ${config.labels[fieldKey as keyof typeof config.labels]?.toLowerCase()} (currently configured)`
                                      : config.placeholders[fieldKey as keyof typeof config.placeholders]
                                  }
                                />
                                {isSecretField && (
                                  <button
                                    type="button"
                                    onClick={() => toggleSecretVisibility(selectedPlatform.id)}
                                    className="flex absolute inset-y-0 right-0 items-center pr-3"
                                    aria-label={showSecrets[selectedPlatform.id] ? `Hide ${config.labels[fieldKey as keyof typeof config.labels]}` : `Show ${config.labels[fieldKey as keyof typeof config.labels]}`}
                                  >
                                    {showSecrets[selectedPlatform.id] ? (
                                      <EyeSlashIcon className="w-5 h-5 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
              ) : (
                                      <EyeIcon className="w-5 h-5 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                                    )}
                                  </button>
                                )}
                              </div>
                              {config.descriptions[fieldKey as keyof typeof config.descriptions] && (
                                <p className="mt-1 text-xs text-theme-text-tertiary dark:text-dark-theme-text-tertiary">
                                  {config.descriptions[fieldKey as keyof typeof config.descriptions]}
                                  {isSecretField && hasCredentialsConfigured(selectedPlatform) && (
                                    <span className="block mt-1">
                                      • Existing secrets are hidden for security. Enter a new value to update.
                                    </span>
                                  )}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Save Button */}
                  <div className="flex justify-end mt-6">
                    <button
                      onClick={() => handleSave(selectedPlatform.id)}
                      disabled={saving[selectedPlatform.id]}
                      className="flex items-center px-6 py-2 space-x-2 text-white rounded-md transition-opacity duration-150 bg-primary-light dark:bg-primary-dark hover:opacity-90 disabled:opacity-50"
                    >
                      <LockClosedIcon className="w-4 h-4" />
                      <span>
                        {saving[selectedPlatform.id] ? 'Saving...' : 'Save Credentials'}
                      </span>
                    </button>
                  </div>
                </div>

                 {/* Cache Preferences Section - Only visible to staff users */}
                 {/* COMMENTED OUT - Cache preferences temporarily disabled
                 {isStaff && (
                 <div className="p-6 rounded-lg border bg-theme-bg dark:bg-dark-theme-bg border-theme-border dark:border-dark-theme-border">
                   <div className="flex items-center mb-4 space-x-3">
                     <ClockIcon className="w-6 h-6 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                     <h4 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
                       Cache Preferences
                     </h4>
                     {hasCustomCacheSettings[selectedPlatform.id] && (
                       <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                         <CogIcon className="mr-1 w-3 h-3" />
                         Custom Settings
                       </span>
                     )}
                   </div>

                   <div className="space-y-4">
                     <div className="flex justify-between items-center">
                       <div className="flex items-center space-x-2">
                         <label className="text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary">
                           Enable Caching for this Platform
                         </label>
                       </div>
                       <button
                         type="button"
                         disabled={savingCache[selectedPlatform.id]}
                         onClick={() => setCacheEnabled(prev => ({ ...prev, [selectedPlatform.id]: !prev[selectedPlatform.id] }))}
                         className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-light focus:ring-offset-2 ${
                           cacheEnabled[selectedPlatform.id] 
                             ? 'bg-primary-light dark:bg-primary-dark' 
                             : 'bg-theme-border dark:bg-dark-theme-border'
                         } ${savingCache[selectedPlatform.id] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                       >
                         <span
                           className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                             cacheEnabled[selectedPlatform.id] ? 'translate-x-6' : 'translate-x-1'
                           }`}
                         />
                       </button>
                     </div>

                     {cacheEnabled[selectedPlatform.id] && (
                       <div>
                         <label htmlFor="cache-duration-select" className="block mb-2 text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary">
                           Cache Duration
                         </label>
                         <select
                           id="cache-duration-select"
                           value={cacheDuration[selectedPlatform.id] || 7}
                           onChange={(e) => setCacheDuration(prev => ({ ...prev, [selectedPlatform.id]: parseInt(e.target.value, 10) }))}
                           disabled={savingCache[selectedPlatform.id]}
                           className="px-4 py-2 w-full rounded-md border border-theme-border dark:border-dark-theme-border bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                           title="Select cache duration for platform data"
                         >
                           <option value={1}>1 day</option>
                           <option value={3}>3 days</option>
                           <option value={7}>7 days</option>
                           <option value={14}>14 days</option>
                           <option value={30}>30 days</option>
                         </select>
                         <p className="mt-1 text-xs text-theme-text-tertiary dark:text-dark-theme-text-tertiary">
                           How long cached data should be considered fresh for this platform
                         </p>
                       </div>
                     )}

                     {!hasCustomCacheSettings[selectedPlatform.id] && (
                       <div className="p-3 bg-blue-50 rounded-md border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
                         <p className="text-xs text-blue-700 dark:text-blue-300">
                           This platform is currently using global cache settings. Save to create platform-specific overrides.
                         </p>
                       </div>
                     )}
                   </div>

                   <div className="flex justify-between mt-6">
                     {hasCustomCacheSettings[selectedPlatform.id] && (
                       <button
                         onClick={() => handleRemoveCachePreferences(selectedPlatform.id)}
                         disabled={savingCache[selectedPlatform.id]}
                         className="flex items-center px-4 py-2 space-x-2 text-white bg-red-600 rounded-md transition-opacity duration-150 dark:bg-red-700 hover:opacity-90 disabled:opacity-50"
                       >
                         <XMarkIcon className="w-4 h-4" />
                         <span>
                           {savingCache[selectedPlatform.id] ? 'Resetting...' : 'Reset to Global'}
                         </span>
                       </button>
                     )}
                     <button
                       onClick={() => handleSaveCachePreferences(selectedPlatform.id)}
                       disabled={savingCache[selectedPlatform.id]}
                       className="flex items-center px-6 py-2 ml-auto space-x-2 text-white rounded-md transition-opacity duration-150 bg-primary-light dark:bg-primary-dark hover:opacity-90 disabled:opacity-50"
                     >
                       <ClockIcon className="w-4 h-4" />
                       <span>
                         {savingCache[selectedPlatform.id] ? 'Saving...' : 'Save Cache Settings'}
                       </span>
                     </button>
                    </div>
                  </div>
                  )}
                  */}

                 {/* User Browser Credentials Section */}
                 {selectedPlatform.requires_browser_automation && (
                   <PlatformCredentialsTab 
                     selectedPlatformId={selectedPlatform.id}
                     onPlatformSelect={setSelectedPlatformId}
                     onError={setError}
                     onSuccess={setSuccess}
                   />
                 )}

                 {/* Platform Info */}
                 <div className="p-4 bg-blue-50 rounded-md border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
                   <div className="flex items-center mb-2 space-x-2">
                     <WifiIcon className="w-5 h-5 text-blue-500" />
                     <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                       Platform Configuration
                     </span>
                   </div>
                   <div className="space-y-1 text-sm text-blue-600 dark:text-blue-400">
                     <p><strong>API URL:</strong> {selectedPlatform.api_url}</p>
                     <p><strong>Production:</strong> {selectedPlatform.is_production_enabled ? 'Enabled' : 'Disabled'}</p>
                     <p><strong>Auth Type:</strong> {selectedPlatform.auth_type}</p>
                     {selectedPlatform.auth_token_url && (
                       <p><strong>Token URL:</strong> {selectedPlatform.auth_token_url}</p>
                     )}
                   </div>
                 </div>
               </div>
             ) : (
                <div className="flex justify-center items-center h-full">
                  <div className="text-center">
                    <ServerStackIcon className="mx-auto mb-4 w-16 h-16 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                    <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary">
                      Select a platform to manage credentials
                    </p>
                  </div>
                </div>
              )}
           </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PlatformCredentialsModal;